import { defineMessages, type MessageDescriptor } from 'react-intl';

export const messages: Record<string, MessageDescriptor> = defineMessages({
	removeBackgroundToolbarLabel: {
		id: 'editor-plugin-malleable-ui.image.remove-background.toolbar-label',
		defaultMessage: 'Remove background',
		description:
			'Label for the AI button in the editor image toolbar that removes the selected image background',
	},
	remixToolbarTooltip: {
		id: 'highlight-actions.remix.inline-toolbar-tooltip',
		defaultMessage: 'Remix',
		description:
			'Tooltip for the Remix button in the editor inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixToolbarLabel: {
		id: 'highlight-actions.remix.inline-toolbar-label',
		defaultMessage: 'Remix',
		description:
			'Label for the Remix button in the editor inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixBlockTooltip: {
		id: 'block-hover.remix.remix-tooltip',
		defaultMessage: 'Remix',
		description:
			'Tooltip for the Remix button in the editor on block hover that opens a modal to remix or regenerate the block content',
	},
	remixBlockLabel: {
		id: 'block-hover.remix.remix-label',
		defaultMessage: 'Remix',
		description:
			'Label for the Remix button in the editor on block hover that opens a modal to remix or regenerate the block content',
	},
	remixDisabledTooltipArchived: {
		id: 'highlight-actions.remix.disabled-tooltip-archived',
		defaultMessage: "Content on archived pages can't be remixed",
		description: 'Tooltip shown on the disabled Remix button when the page is archived',
	},
	remixDisabledTooltipVersionHistory: {
		id: 'highlight-actions.remix.disabled-tooltip-version-history',
		defaultMessage: 'Remix is unavailable in version history',
		description:
			'Tooltip shown on the disabled Remix button when viewing a previous version of the page',
	},
	remixInProgressTooltip: {
		id: 'highlight-actions.remix.in-progress-tooltip',
		defaultMessage: 'Remix in progress...',
		description:
			'Tooltip shown on the disabled Remix button when a remix session is already in progress',
	},
	editInProgressTooltip: {
		id: 'editor-plugin-malleable-ui.editInProgressTooltip',
		defaultMessage: 'Edit is in progress.',
		description:
			'Tooltip shown on a disabled Remix or Create button while a Rovo edit is in progress',
	},
	remixToolbarTooltipUpdated: {
		id: 'highlight-actions.remix.inline-toolbar-tooltip-updated',
		defaultMessage: 'Create a diagram, infographic, and more from this content',
		description:
			'Updated tooltip for the Remix button in the editor inline toolbar that opens a modal to remix or regenerate the block content into a diagram, infographic, or other format',
	},
	remixBlockTooltipUpdated: {
		id: 'block-hover.remix.remix-tooltip-updated',
		defaultMessage: 'Create a visual from this content with Remix',
		description:
			'Updated tooltip for the Remix button in the editor on block hover that opens a modal to remix or regenerate the block content into a diagram, infographic, or other format',
	},
	flagFinalizeSuccessTitle: {
		id: 'editor-plugin-malleable-ui.maui.finalize.flag.success.title',
		defaultMessage: 'Whiteboard moved',
		description:
			'Toast title shown after the in-embed MAUI diagram whiteboard has been moved to a location where other people can view it.',
	},
	flagFinalizeSuccessDescriptionCurrentPage: {
		id: 'editor-plugin-malleable-ui.maui.finalize.flag.success.description.currentPage',
		defaultMessage: 'Your diagram’s whiteboard now lives under the current content.',
		description:
			'Toast description shown after the in-embed MAUI diagram whiteboard is moved under the current Confluence content without using the destination picker.',
	},
	flagFinalizeSuccessDescriptionPickedSpace: {
		id: 'editor-plugin-malleable-ui.maui.finalize.flag.success.description.pickedSpace',
		defaultMessage: 'Your diagram’s whiteboard now lives in the space you selected.',
		description:
			'Toast description shown after the in-embed MAUI diagram whiteboard is moved to a space the user picked via the destination picker.',
	},
	flagFinalizeSpacePickerTitle: {
		id: 'editor-plugin-malleable-ui.maui.finalize.flag.spacePicker.title',
		defaultMessage: 'Choose where to save whiteboard',
		description:
			'Toast title shown when the current Confluence space cannot accept the MAUI diagram whiteboard and the user needs to choose another location.',
	},
	flagFinalizeSpacePickerDescription: {
		id: 'editor-plugin-malleable-ui.maui.finalize.flag.spacePicker.description',
		defaultMessage: 'Select a location for this diagram’s whiteboard so others can view it.',
		description:
			'Toast description shown when the user must pick a destination location for a MAUI diagram whiteboard so other people can view it.',
	},
	mauiDiagramFinalizeErrorTitle: {
		id: 'editor-plugin-malleable-ui.mauiDiagramFinalizeErrorTitle',
		defaultMessage: "Couldn't save diagram to this page",
		description: 'Title for the error flag shown when saving a MAUI diagram to this page fails.',
	},
	mauiDiagramFinalizeErrorDescription: {
		id: 'editor-plugin-malleable-ui.mauiDiagramFinalizeErrorDescription',
		defaultMessage:
			'Something went wrong while saving this diagram. Please check your permissions and try again.',
		description:
			'Description for the error flag shown when saving a MAUI diagram to this page fails.',
	},
	mauiDiagramFinalizeMissingPageDescription: {
		id: 'editor-plugin-malleable-ui.mauiDiagramFinalizeMissingPageDescription',
		defaultMessage:
			'Could not determine the current page. Please save the page first and try again.',
		description:
			'Description for the error flag shown when a MAUI diagram cannot be saved because the current page could not be determined.',
	},
	mauiDiagramMoveErrorTitle: {
		id: 'editor-plugin-malleable-ui.mauiDiagramMoveErrorTitle',
		defaultMessage: 'Could not move diagram',
		description:
			'Title for the legacy error flag shown when moving a pasted MAUI diagram to the current space fails.',
	},
	mauiDiagramMoveErrorDescription: {
		id: 'editor-plugin-malleable-ui.mauiDiagramMoveErrorDescription',
		defaultMessage:
			"You don't have permission to add content to this space. Paste it in a different space to make your diagram visible to others and avoid auto removal.",
		description:
			'Description for the legacy error flag shown when moving a pasted MAUI diagram to the current space fails.',
	},
	mauiDiagramPasteErrorTitle: {
		id: 'editor-plugin-malleable-ui.mauiDiagramPasteErrorTitle',
		defaultMessage: 'Could not paste diagram',
		description:
			'Title for the error flag shown when pasting a MAUI diagram into the current space fails.',
	},
	mauiDiagramPasteErrorDescription: {
		id: 'editor-plugin-malleable-ui.mauiDiagramPasteErrorDescription',
		defaultMessage:
			"You don't have permission to add content to this space. Paste it in a different space.",
		description:
			'Description for the error flag shown when pasting a MAUI diagram into the current space fails.',
	},
	mauiAppDuplicatePermissionErrorTitle: {
		id: 'editor-plugin-malleable-ui.mauiAppDuplicatePermissionErrorTitle',
		defaultMessage: "You don't have permission to duplicate this component",
		description:
			'Title for the error flag shown when duplicating a MAUI app fails because of missing permissions.',
	},
	mauiAppDuplicateErrorTitle: {
		id: 'editor-plugin-malleable-ui.mauiAppDuplicateErrorTitle',
		defaultMessage: 'Could not duplicate component',
		description: 'Title for the error flag shown when duplicating a MAUI app fails.',
	},
	mauiAppDuplicateErrorDescription: {
		id: 'editor-plugin-malleable-ui.mauiAppDuplicateErrorDescription',
		defaultMessage: "We couldn't duplicate this component. Please try again.",
		description: 'Description for the error flag shown when duplicating a MAUI app fails.',
	},
	subtypeAdjustedFlagTitle: {
		id: 'editor-plugin-malleable-ui.subtypeAdjustedFlagTitle',
		defaultMessage: 'Output adjusted',
		description:
			"Title of the informational flag shown after a remix completes when the user's selected output type was not compatible, so Rovo generated a different one instead",
	},
	subtypeAdjustedFlagDescription: {
		id: 'editor-plugin-malleable-ui.subtypeAdjustedFlagDescription',
		defaultMessage: "Your selection wasn't compatible with that output, so Rovo picked a new one.",
		description:
			"Body of the informational flag shown after a remix completes when the user's selected output type was not compatible, explaining that Rovo chose a different output type instead",
	},
});
